<template>
    <div id="print-queue-container">
        <card title="Call Log">
            <template #header-right>
                <b-button v-if="canManageQuestionSets" variant="secondary" size="sm" @click="showQuestionSetsModal = true">
                    <i class="fas fa-cog"></i> Manage Audit Forms
                </b-button>
            </template>
            <b-form class="filter-grid mb-3" @submit.prevent="searchCalls">
                <b-row>
                    <b-col>
                        <SearchComponent v-model="userFilter" label="Agent" :option-label="userLabel"
                            search-u-r-l="/api/Call/GetAllAgents" prepopulate clearable />
                    </b-col>
                    <b-col>
                        <fg-input v-model="externalNumber" label="External Number:" />
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="patientFilter" label="Patient" :option-label="patientLabel"
                            search-u-r-l="/Patient" clearable />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>

                        <div>
                            <label>Call Date:</label>
                            <div class="d-flex" style="gap: 15px;">
                                <el-date-picker size="sm" v-model="startDate" today-button reset-button
                                    format="MM/dd/yyyy" type="date" />
                                <el-date-picker size="sm" v-model="endDate" today-button reset-button
                                    format="MM/dd/yyyy" type="date" />
                            </div>
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button type="submit" variant="primary">Search</b-button>
                    </b-col>
                </b-row>
            </b-form>
        </card>
        <card>
            <b-table id="CallLog" striped responsive="true" sort-icon-left :items="calls" :fields="fields">
                <template #cell(recordingSid)="data">
                    <i :class="{ 'fa fa-fw fa-chevron-right text-primary': true, 'fa-rotate-90': data.detailsShowing }"
                        role="button" @click="selectRecording(data)" style="transition: transform .15s;"></i>
                </template>
                <template #cell(patient)="data">
                    <router-link v-if="data.item.patientId && (data.item.patientFirstName || data.item.patientLastName)"
                        :to="`/processing/patient-dashboard/${data.item.patientId}`">
                        {{ data.item.patientFirstName }} {{ data.item.patientLastName }}
                    </router-link>
                </template>
                <template #cell(auditActions)="data">
                    <div class="d-flex gap-2">
                        <b-button v-if="canCompleteAudits && !data.item.auditCompletedAt"
                            variant="success" size="sm" @click="startAudit(data.item)">
                            <i class="fas fa-clipboard-check"></i> Audit
                        </b-button>
                        <b-button v-if="canViewAudits && data.item.auditCompletedAt"
                            variant="info" size="sm" @click="viewAudit(data.item)">
                            <i class="fas fa-eye"></i> View Audit
                        </b-button>
                    </div>
                </template>
                <template #row-details="row">
                    <card plain class="bg-white">
                        <h6 style="font-weight: 500">
                            Transcription
                            <i class="fas fa-download" style="color: blue; cursor:pointer"
                                @click="downloadCall(row.item.recordingSid)"></i>
                        </h6>
                        <audio :id="`audio-${row.item.recordingSid}`" controls hidden>
                            <source :id="`source-${row.item.recordingSid}`" type="audio/wav" src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" />
                        </audio>
                        <transcription-viewer entityType="0" :entityId="row.item.id" @transcriptclick="transcriptClicked" />
                    </card>
                </template>
            </b-table>
        </card>

        <!-- Audit Modal -->
        <CallLogAuditModal v-model="showAuditModal" :call="selectedCall" @audit-submitted="onAuditSubmitted" />

        <!-- Audit Viewer Modal -->
        <CallLogAuditViewer v-model="showAuditViewerModal" :call="selectedCall" />

        <!-- Question Sets Management Modal -->
        <b-modal v-model="showQuestionSetsModal" title="Manage Call Log Audit Forms" size="xl" scrollable hide-footer>
            <TaskQuestionSets :entity-type="callLogEntityType" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import {Component, Vue} from 'vue-property-decorator';
import moment from 'moment';
import SearchComponent from '@/components/SearchComponent.vue';
import {DatePicker} from "element-ui";
import Axios, {AxiosRequestConfig} from "axios";
import {Call} from '@/models/Call';
import {Patient} from '@/models/Patient';
import TranscriptionViewer from '@/components/TranscriptionViewer.vue';
import {NotificationOptions} from '@/util/NotificationOptionsPresets';
import CallLogAuditModal from '@/components/CallLog/CallLogAuditModal.vue';
import CallLogAuditViewer from '@/components/CallLog/CallLogAuditViewer.vue';
import TaskQuestionSets from '@/components/Tasks/TaskQuestionSets.vue';
import { EntityType } from '@/models/EntityType';

@Component({
    name: "CallLog",
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
        TranscriptionViewer,
        CallLogAuditModal,
        CallLogAuditViewer,
        TaskQuestionSets,
    }
})
export default class CallLogPage extends Vue {
    protected startDate?: string = moment().add(-30, 'day').toISOString();
    protected endDate?: string = moment().toISOString();
    protected externalNumber: string = "";
    protected patientFilter: Patient | null = null;
    protected isLoading?: boolean = false;
    protected userFilter: Call = new Call();
    protected calls: Array<Call> = [];
    protected fields: any[] = [
        { key: "recordingSid", label: "" },
        { key: "externalNumber", label: "External Number", sortable: true },
        { key: "agent", label: "Agent", sortable: true },
        { key: "patient", label: "Patient", sortable: true },
        { key: "initiated", label: "Initiated", sortable: true, formatter: (d: Date) => moment(d).local().format("L LT") },
        { key: "duration", label: "Duration", sortable: true },
        { key: "outbound", label: "Direction", sortable: true, formatter: (d: boolean) => d ? "Outbound" : "Inbound" },
        { key: "auditActions", label: "Audit", class: "audit-actions" },
    ];

    // Audit functionality properties
    protected selectedCall: Call | null = null;
    protected showAuditModal = false;
    protected showAuditViewerModal = false;
    protected showQuestionSetsModal = false;
    protected callLogEntityType = EntityType.CallLog;

    userLabel(c:any) {
        return c.agent ? `${c.agent}` : '';
    }

    patientLabel(p:any) {
        return p.id ? `ID: ${ p.id } ${ p.lastName }, ${ p.firstName }` : ''
    }

    mounted() {
        const phone = this.$route.query["phone"] as string;
        if (phone) {
            this.externalNumber = phone.replace("+", "").trim();
            this.searchCalls();
        }
    }

    async searchCalls() {
        try {
            const response = await Axios.get('/api/Call', {
                params: {
                    startDate: this.startDate,
                    endDate: this.endDate,
                    externalNumber: this.externalNumber,
                    agent: this.userFilter?.agent,
                    patientId: this.patientFilter?.id || null
                }
            });
            this.calls = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }


    selectRecording(r: any) {
        r.toggleDetails();
    }

    async downloadCall(itemId: string) {
        try {
            const config: AxiosRequestConfig = {
                responseType: 'blob'
            }
            const response = await Axios.get(`/Call/DownloadAudio/${itemId}`, config);
            const url = window.URL.createObjectURL(new Blob([response.data], { type: "audio/wav" }));
            const sourceItem = document.getElementById(`source-${itemId}`) as HTMLSourceElement;
            sourceItem.src = url;
            const audioItem = document.getElementById(`audio-${itemId}`) as HTMLAudioElement;
            audioItem.hidden = false;
            audioItem.pause();
            audioItem.load();
            audioItem.play();
            console.trace(response);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    transcriptClicked(voicemailId: number, startTime: any) {
        const timespan = moment.duration(startTime);
        const matchedCalls = this.calls.filter(c=>c.id == voicemailId);
        if (matchedCalls.length == 0) {
            console.error(`No call found for voicemail ${voicemailId}`);
            return;
        }

        const itemId = matchedCalls[0].recordingSid;
        const audioItem = document.getElementById(`audio-${itemId}`) as HTMLAudioElement;

        if (audioItem.getAttribute('hidden')) {
            this.downloadCall(itemId);
        }

        audioItem.currentTime = timespan.seconds();
    }

    // Computed properties for permissions
    get canManageQuestionSets(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Admin/Call Log Question Sets');
    }

    get canCompleteAudits(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Reports/Call Log Audits');
    }

    get canViewAudits(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Reports/View Call Log Audits');
    }

    // Audit functionality methods

    startAudit(call: Call) {
        this.selectedCall = call;
        this.showAuditModal = true;
    }

    viewAudit(call: Call) {
        this.selectedCall = call;
        this.showAuditViewerModal = true;
    }

    async onAuditSubmitted(callId: number) {
        // Update the call object with audit completion
        const call = this.calls.find(c => c.id === callId);
        if (call) {
            call.auditCompletedAt = new Date();
            this.$forceUpdate(); // Force reactivity update
        }
    }
}
</script>

<style scoped>
::v-deep(.audit-actions) {
    width: 150px;
    text-align: center;
}

.d-flex.gap-2 {
    gap: 0.5rem;
}
</style>
